import { useEffect, useRef, type RefObject } from "react";
import { getQueryParam } from "../../../utils/general";

/**
 * Deep-link scroll for PublishedPageV3.
 *
 * Other surfaces link into the published page with a `scrollIntoViewElemId`
 * query param (built by `getRoutePathToPublishedContent` in ootils). There are
 * two kinds of target, distinguished only by what the value resolves to in the
 * DOM:
 *
 *   1. Annotation Explorer  → `scrollIntoViewElemId = annoKey`. annoKey is the
 *      key of the field's `annoData` map (see okf-sub AnnotationConverter), which
 *      IS the annoDataId — the same id AnnoPlugin stamps onto each `<mark>` and
 *      that V3 puts on each sidebar card's `data-anno-id`. So we route it through
 *      the existing `onAnnotation` (handleAnnotationClick) handler, which scrolls
 *      the mark into view AND centres its sidebar card — exactly like a click.
 *
 *   2. AI Chat chunk ref    → `scrollIntoViewElemId = valuePath`. That's a field
 *      path, not an annotation. ViewBlockGenerator stamps it as `data-valuepath`
 *      on the block wrapper, so we just scroll that field into view and flash it.
 *
 * V2 did the equivalent via `scrollToElement` (querySelector data-valuepath ||
 * getElementById). V3's marks/cards appear a few frames after the editor mounts
 * (AnnoPlugin stamps mark ids from an async mutation listener), so we poll with
 * requestAnimationFrame until the target exists or a frame budget runs out.
 */

// ~2s at 60fps — generous headroom for AnnoPlugin to stamp mark ids after mount.
const MAX_FRAMES = 120;
// Brief highlight tint for the field-level (AI chat) target, then fade back.
const FLASH_COLOR = "rgba(255, 214, 0, 0.35)";

const scrollAndFlash = (el: HTMLElement) => {
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  const prevBg = el.style.backgroundColor;
  const prevTransition = el.style.transition;
  el.style.transition = "background-color 0.3s ease-out";
  el.style.backgroundColor = FLASH_COLOR;
  setTimeout(() => {
    el.style.backgroundColor = prevBg;
    setTimeout(() => {
      el.style.transition = prevTransition;
    }, 300);
  }, 1000);
};

type Params = {
  /** getContributionStatus — gate so the target DOM exists before we look. */
  status: string;
  /** location.search — source of the `scrollIntoViewElemId` param. */
  search: string;
  /** The V3 content grid; both marks and field wrappers live inside it. */
  gridRef: RefObject<HTMLElement | null>;
  /** handleAnnotationClick — scrolls the mark + centres its sidebar card. */
  onAnnotation: (annoId: string) => void;
};

export const useScrollToDeepLinkTarget = ({
  status,
  search,
  gridRef,
  onAnnotation,
}: Params) => {
  // One-shot per mount: a deep link is a landing action, not a re-runnable one.
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (status !== "success") return;

    const targetId = getQueryParam(search, "scrollIntoViewElemId");
    if (!targetId) return;

    firedRef.current = true;

    const safe = targetId.replace(/"/g, '\\"');
    let frames = 0;
    let rafId = 0;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      const root = gridRef.current;
      if (root) {
        // 1. Annotation target (explorer): annoKey === the mark's stamped id.
        const mark = root.querySelector<HTMLElement>(`mark[id="${safe}"]`);
        if (mark) {
          onAnnotation(targetId);
          return;
        }
        // 2. Field target (AI chat chunk ref): valuePath === data-valuepath.
        const field = root.querySelector<HTMLElement>(
          `[data-valuepath="${safe}"]`,
        );
        if (field) {
          scrollAndFlash(field);
          return;
        }
      }
      // Target not in the DOM yet (marks stamp a few frames after mount) —
      // keep polling until it appears or we exhaust the budget.
      if (frames++ < MAX_FRAMES) rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [status, search, gridRef, onAnnotation]);
};
